import { FormEvent, useCallback, useEffect, useRef, useState } from 'react'
import { QrScannerModal } from './QrScannerModal'
import {
  addScannedMachine,
  checkDuplicateScan,
  deleteScannedMachine,
  listScannedMachines,
  type DuplicateScanWarning,
  type ScannedMachineDraft,
} from '../lib/scannerDb'
import {
  buildScanExport,
  scanExportFilename,
  serializeScanExport,
} from '../lib/scannerExport'
import { normalizeQr } from '../lib/machines'

type Step = 'idle' | 'details'

interface Props {
  onBack: () => void
  onToast?: (message: string) => void
}

function formatDupWarning(dup: DuplicateScanWarning): string {
  const parts: string[] = []
  const catalogNames = dup.catalogNames?.length
    ? dup.catalogNames
    : dup.catalogName
      ? [dup.catalogName]
      : []
  if (catalogNames.length === 1) {
    parts.push(`Already in the app catalog as "${catalogNames[0]}"`)
  } else if (catalogNames.length > 1) {
    parts.push(
      `Already in the app catalog as ${catalogNames.map((n) => `"${n}"`).join(' and ')}`,
    )
  }
  if (dup.drafts.length === 1) {
    parts.push(`Already scanned as "${dup.drafts[0].name}"`)
  } else if (dup.drafts.length > 1) {
    parts.push(`Already scanned ${dup.drafts.length} times`)
  }
  return `${parts.join('. ')}. You can still save another entry if this machine maps to a different workout.`
}

export function ScannerPage({ onBack, onToast }: Props) {
  const [step, setStep] = useState<Step>('idle')
  const [scanOpen, setScanOpen] = useState(true)
  const [qrPayload, setQrPayload] = useState<string | null>(null)
  const [dupWarning, setDupWarning] = useState<string | null>(null)
  const [name, setName] = useState('')
  const [imageFile, setImageFile] = useState<File | null>(null)
  const [imagePreview, setImagePreview] = useState<string | null>(null)
  const [drafts, setDrafts] = useState<ScannedMachineDraft[]>([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  const imageInputRef = useRef<HTMLInputElement | null>(null)

  const refresh = useCallback(async () => {
    setDrafts(await listScannedMachines())
  }, [])

  useEffect(() => {
    void refresh()
  }, [refresh])

  useEffect(() => {
    return () => {
      if (imagePreview) URL.revokeObjectURL(imagePreview)
    }
  }, [imagePreview])

  const resetForm = () => {
    setStep('idle')
    setQrPayload(null)
    setDupWarning(null)
    setName('')
    setImageFile(null)
    if (imagePreview) URL.revokeObjectURL(imagePreview)
    setImagePreview(null)
    setError(null)
    if (imageInputRef.current) imageInputRef.current.value = ''
  }

  const onScan = useCallback(async (payload: string) => {
    const normalized = normalizeQr(payload)
    setQrPayload(normalized)
    setError(null)
    setMessage(null)
    const dup = await checkDuplicateScan(normalized)
    setDupWarning(dup ? formatDupWarning(dup) : null)
    setStep('details')
  }, [])

  const onPickImage = (file: File | undefined) => {
    if (!file) return
    if (!file.type.startsWith('image/')) {
      setError('Pick an image of the muscle diagram.')
      return
    }
    setError(null)
    setImageFile(file)
    if (imagePreview) URL.revokeObjectURL(imagePreview)
    setImagePreview(URL.createObjectURL(file))
  }

  const onSave = async (e: FormEvent) => {
    e.preventDefault()
    if (!qrPayload) {
      setError('Scan a QR code first.')
      return
    }
    const trimmed = name.trim()
    if (!trimmed) {
      setError('Enter a machine name.')
      return
    }
    if (!imageFile) {
      setError('Add a photo of the muscle diagram.')
      return
    }
    setBusy(true)
    setError(null)
    try {
      await addScannedMachine({
        name: trimmed,
        qrPayload,
        muscleImage: imageFile,
        muscleImageMime: imageFile.type || 'image/jpeg',
      })
      await refresh()
      onToast?.(`Saved ${trimmed}`)
      setMessage(`Saved "${trimmed}". Scan another when ready.`)
      resetForm()
      setScanOpen(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save machine.')
    } finally {
      setBusy(false)
    }
  }

  const onExport = async () => {
    setError(null)
    if (!drafts.length) {
      setError('Nothing to export yet.')
      return
    }
    setBusy(true)
    try {
      const exp = await buildScanExport(drafts)
      const blob = new Blob([serializeScanExport(exp)], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = scanExportFilename(exp.exportedAt)
      a.click()
      URL.revokeObjectURL(url)
      setMessage(
        `Exported ${exp.machines.length} machine${exp.machines.length === 1 ? '' : 's'}.`,
      )
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not export.')
    } finally {
      setBusy(false)
    }
  }

  const onDelete = async (id: string, label: string) => {
    const ok = window.confirm(`Remove scanned machine "${label}" from this phone?`)
    if (!ok) return
    await deleteScannedMachine(id)
    await refresh()
    setMessage(`Removed "${label}".`)
  }

  return (
    <section className="panel scanner-page" aria-labelledby="scanner-title">
      <header className="history-toolbar">
        <button
          type="button"
          className="history-back-btn"
          onClick={onBack}
          aria-label="Back to muscle map"
        >
          <span aria-hidden="true">←</span>
        </button>
        <div className="history-toolbar-copy">
          <h1 id="scanner-title">Machine scanner</h1>
          <p className="muted small">Catalog gym machines for later import</p>
        </div>
      </header>

      <div className="settings-stack">
        <section className="settings-block" aria-labelledby="scan-flow-title">
          <h2 id="scan-flow-title">New machine</h2>
          <p className="muted small">
            Scan the sticker QR, photograph the muscle diagram, then name it. Duplicates warn but are
            allowed when one machine supports more than one workout.
          </p>

          {step === 'idle' ? (
            <div className="settings-actions">
              <button type="button" className="btn-primary" onClick={() => setScanOpen(true)}>
                Scan QR
              </button>
            </div>
          ) : (
            <form className="stack scanner-form" onSubmit={(e) => void onSave(e)}>
              {dupWarning ? (
                <p className="scanner-warn" role="status">
                  {dupWarning}
                </p>
              ) : null}

              <p className="muted small scanner-qr-preview">
                QR: <code>{qrPayload}</code>
              </p>

              <label>
                Muscle diagram photo
                <input
                  ref={imageInputRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={(e) => onPickImage(e.target.files?.[0])}
                />
              </label>
              {imagePreview ? (
                <img
                  src={imagePreview}
                  alt="Muscle diagram preview"
                  className="scanner-preview"
                />
              ) : null}

              <label>
                Machine name
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Seated row"
                  autoComplete="off"
                  required
                />
              </label>

              {error ? (
                <p className="form-error" role="alert">
                  {error}
                </p>
              ) : null}

              <div className="settings-actions">
                <button type="submit" className="btn-primary" disabled={busy}>
                  {busy ? 'Saving…' : 'Save'}
                </button>
                <button
                  type="button"
                  className="btn-ghost"
                  disabled={busy}
                  onClick={() => {
                    resetForm()
                    setScanOpen(true)
                  }}
                >
                  Rescan QR
                </button>
              </div>
            </form>
          )}
        </section>

        <section className="settings-block" aria-labelledby="drafts-title">
          <div className="panel-head">
            <h2 id="drafts-title">Saved on this phone</h2>
            <button
              type="button"
              className="btn-ghost"
              disabled={busy || drafts.length === 0}
              onClick={() => void onExport()}
            >
              Export JSON
            </button>
          </div>
          <p className="muted small">
            Export includes QR payloads, names, and muscle photos (base64) for AI processing into the
            main catalog later.
          </p>

          {message && !error ? (
            <p className="settings-message" role="status">
              {message}
            </p>
          ) : null}
          {error && step === 'idle' ? (
            <p className="settings-error" role="alert">
              {error}
            </p>
          ) : null}

          {drafts.length === 0 ? (
            <p className="muted">No scanned machines yet.</p>
          ) : (
            <ul className="scanner-draft-list">
              {drafts.map((d) => (
                <li key={d.id}>
                  <div>
                    <strong>{d.name}</strong>
                    <span className="muted small">
                      {new Date(d.createdAt).toLocaleString()} · QR saved
                    </span>
                  </div>
                  <button
                    type="button"
                    className="linkish danger"
                    onClick={() => void onDelete(d.id, d.name)}
                  >
                    Delete
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      <QrScannerModal
        open={scanOpen}
        onClose={() => setScanOpen(false)}
        onScan={(payload) => void onScan(payload)}
        readerId="scanner-qr-reader"
        showDemoMachines={false}
        title="Scan machine QR"
      />
    </section>
  )
}
